import { ff } from '@atlassian/jira-feature-flagging';
import type { FlattenedCommand, OrderedFilteredCommands } from '../../types';
import type { CommandMatches, FilteredCommands, ListedCommand } from '../../types/commands';
import { SEARCH_SECTION_PRIORITY_OFFSET_STRENGTH } from './constants';

const getSectionOffset = <T extends { priority?: number }>(section: T) =>
	1 + (section.priority ?? 0) * SEARCH_SECTION_PRIORITY_OFFSET_STRENGTH;

export const getFlattenedResults = (
	groupedCommands: OrderedFilteredCommands | FilteredCommands, // TODO : remove FilteredCommands when clenaing up compal-override-filter-function
	activeIndex: number,
): CommandMatches => {
	const ungroupedCommands = groupedCommands.ungrouped.slice(0);
	if (!ff('compal-override-filter-function')) {
		ungroupedCommands.sort(
			(a, b) => (a.command.searchRelevancyScore ?? 1) - (b.command.searchRelevancyScore ?? 1),
		);
	}
	let results: FlattenedCommand[] = ungroupedCommands;

	const sortedGroups = ff('compal-override-filter-function')
		? Object.values(groupedCommands.grouped)
		: Object.entries(groupedCommands.grouped)
				.map(([, group]) => group)
				.map((group) => ({
					...group,
					commands: group.commands.sort(
						(a, b) => (a.command.searchRelevancyScore ?? 1) - (b.command.searchRelevancyScore ?? 1),
					),
				}))
				.sort((a, b) => {
					const aScore =
						Math.min(...a.commands.map((command) => command.command.searchRelevancyScore ?? 1), 1) *
						getSectionOffset(a);
					const bScore =
						Math.min(...b.commands.map((command) => command.command.searchRelevancyScore ?? 1), 1) *
						getSectionOffset(b);
					if (aScore !== bScore) {
						return aScore - bScore;
					}

					const priorityA = a.priority || 0;
					const priorityB = b.priority || 0;

					return priorityA - priorityB;
				});

	sortedGroups.forEach(({ id, group, renderGroup, commands: groupCommands }) => {
		results = results.concat(
			{
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				...(renderGroup ? { renderGroup } : { group: group as string }),
				isGroup: true,
				id,
			},
			groupCommands,
		);
	});

	const hasAction = (listed: ListedCommand) =>
		Boolean(listed.command.primaryAction || listed.command.secondaryAction);

	let index = 0;
	let totalActionableCommands = 0;
	let activeCommand;
	let sectionIndex = -1;
	let indexWithinSection = 0;
	let sectionId: string | undefined;
	results = results.map((result) => {
		if (result.isGroup) {
			const newResult = result;
			indexWithinSection = 0;
			sectionIndex += 1;
			newResult.index = sectionIndex;
			sectionId = result.id;
		}
		if (!result.isGroup) {
			const newResult = result;
			if (hasAction(result)) {
				newResult.index = index;
				index += 1;
				totalActionableCommands += 1;
				newResult.active = activeIndex === result.index;
			}

			newResult.indexWithinSection = indexWithinSection;
			newResult.sectionIndex = sectionIndex;
			newResult.sectionId = sectionId;
			indexWithinSection += 1;
			if (newResult.active) {
				activeCommand = newResult.command;
			}
			return newResult;
		}

		return result;
	});

	return {
		results,
		total: totalActionableCommands,
		activeCommand,
	};
};
